import * as React from 'react';
import { Toggler } from '../../Toggler/toggler';
import { Schema, SchemaField } from '../../../types/Metadata';
import { Group } from 'rcd-cpp-ui/ui/form/Group';
import { Typeahead } from '../../Typeahead/typeahead';
import { FieldType } from '../../../enums/FieldType';
import { DataType } from '../../../enums/DataType';
import { Pencil, Revert, SixDots, TrashSmallGrey } from '../../Icon/icon';
import { useActions } from '../schemaModalActions';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { NodeType } from '../../../enums/NodeType';
import { WYSIWYGEditor } from '../../WYSIWYGEditor/WYSIWYGEditor';
import { FieldRequiredValue } from '../../../enums/FieldRequiredValue';
import { Icon } from 'rcd-cpp-ui/ui/icon';
import TooltipPopover from '../../Popover/popover';
import {
  FieldTypeOptions,
  StringTypeOpions,
  getRelationTypes,
  Selection,
  ItemRequiredOptions,
  isRequireDisabled,
  isShowDisabled
} from '../FieldCard/fieldCard';
import { useSelector } from '../../../utils/useSelector';
import {
  audienceDetailsFields,
  cn,
  dndCardDetails,
  getSchema,
  isAuditType
} from '../../../utils';
import { EnumInput } from '../../EnumInput/enumInput';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';
import { FieldShowValue } from '../../../enums/FieldShowValue';
import { PermissionField } from '../FieldCard/permissionField';
import { ItemType } from '../../../enums/ItemType';
import { ReviewDone } from '../../../enums/ReviewDone';

interface Props {
  field: SchemaField;
  treeFields?: SchemaField[];
  isDisabled: boolean;
  index: number;
  schemaForm?: Schema;
  onMove?: (index1: number, index2: number) => void;
  children?: React.ReactNode;
}

const getFieldTypeName = ({ fieldType, dataType }: SchemaField) =>
  FieldTypeOptions.filter(f => {
    if (f.fieldType == FieldType.reference) return fieldType == f.fieldType;
    return f.dataType == dataType;
  })[0]?.name;

export const ItemFieldCard = (p: Props) => {
  const itemTypes = useSelector(s => s.planning?.itemTypes);
  const schema = useSelector(s =>
    getSchema(s.planning?.selectedWorkspace?.schemas, NodeType.Item)
  );
  const schemaForms = useSelector(s => s.schemaModal?.schemaForms);
  const selected = useSelector(s => s.schemaModal?.selectedField);
  const additionalInfo = useSelector(s => s.schemaModal?.additionalInfo);
  const dataTypes = useSelector(s =>
    s.planning?.selectedWorkspace?.schemas
      ?.map(s => ({
        dataType: s.props.type as any,
        name: s.props.name
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(dt => dt.dataType != NodeType.Comment)
  );
  const displayField = schema?.props?.displayField;
  const orderField = useSelector(
    s => findOne(s.schemaModal?.fields, { isOrderField: true })?.field
  );
  const adminWorkspaceRefId = useSelector(
    s =>
      s.planning?.workspaces?.find(w =>
        w?.labels?.includes(NodeType.AdminWorkspace)
      )?.props?.refId
  );
  const { selectField, removeField } = useActions();

  const { index, onMove, schemaForm, treeFields } = p;
  const {
    fieldType,
    dataType,
    relationType,
    field,
    name,
    requiredAt,
    showAt,
    description,
    multiValue,
    isSystemField,
    isInherited,
    refId,
    alwaysShowIn
  } = p.field;
  const isSelected = selected == field;
  const isManagePage = schemaForm != null;
  const isConfigurePage = schemaForm == null;

  const ref = React.useRef(null);
  React.useEffect(() => {
    if (isSelected && ref?.current) {
      ref.current.scrollIntoView();
    }
  }, [ref, isSelected]);

  const canConvertType =
    dataType == DataType.string || dataType == DataType.longString;
  let errMsg = null;
  additionalInfo?.forEach(e => {
    if (!!e.message && e.field == field) {
      errMsg = e.message;
    }
  });

  const baseSchemaField = findOne(schema?.baseSchema?.props?.fields, { field });
  const baseSchemaIsSystem =
    adminWorkspaceRefId == schema?.baseSchema?.workspaceRefId;
  const inheritedName = baseSchemaField?.name;
  const inheritedDesc = !treeFields?.length
    ? baseSchemaField?.description
    : description;
  const { updateField, handleShowInOnChange } = useActions();
  const [isSystemTitleEditable, setIsSystemTitleEditable] =
    React.useState<boolean>(isConfigurePage && inheritedName != name);
  const [isSystemDescriptionEditable, setIsSystemDescriptionEditable] =
    React.useState<boolean>(isConfigurePage && inheritedDesc != description);
  const fieldTypeName = getFieldTypeName(p.field);
  const selectedDataTypeName = findOne(dataTypes, { dataType })?.name;

  const isOrderField = orderField == field || displayField == field;

  const showInValues = schemaForms?.reduce((vals, schemaForm) => {
    if (findOne(schemaForm?.props?.fields, { field })) {
      vals.push(schemaForm.props.itemType);
    }
    return vals;
  }, []);

  const inheritedShowInValues = schema?.baseSchema?.schemaForms?.reduce(
    (vals, schemaForm) => {
      if (findOne(schemaForm?.props?.fields, { field })) {
        vals.push(schemaForm.props.itemType);
      }
      return vals;
    },
    []
  );

  const itemRequiredOptions = React.useMemo(() => {
    const itemType = schemaForm?.props?.itemType;
    if (!itemType) return ItemRequiredOptions;
    return ItemRequiredOptions.map(o => {
      if (o.value == FieldRequiredValue.ReviewDone) {
        return {
          ...o,
          label:
            itemType == ItemType.content
              ? ReviewDone.StartProduction
              : ReviewDone.SubmitToConf
        };
      }
      return o;
    });
  }, [schemaForm?.props?.itemType]);

  const requiredValue = findOne(itemRequiredOptions, { value: requiredAt });
  const showValue = findOne(ItemManageShowOptions, { value: showAt });

  const disableType = !!refId && !canConvertType;
  let disableShowIn = ['title', 'status', 'seriesBy', 'configureWorkflowType']
    .concat(audienceDetailsFields)
    .includes(field);
  if (treeFields?.length > 1 && treeFields[0].field != field) {
    disableShowIn = true;
  }
  const isFieldSettingEdited = (
    field: SchemaField,
    baseField: SchemaField,
    keys: string[]
  ) => {
    if (!field || !baseField) return false;
    const configureDiff =
      keys.some(key => field[key] !== baseField[key]) ||
      (!disableShowIn &&
        JSON.stringify((showInValues || []).slice().sort()) !==
          JSON.stringify((inheritedShowInValues || []).slice().sort()));
    let manageDiff = false;
    for (const schemaForm of schemaForms) {
      const baseSchemaForm = schema?.baseSchema?.schemaForms?.find(
        f => f.props.itemType == schemaForm.props.itemType
      );
      if (!baseSchemaForm) continue;
      const thisField = findOne(schemaForm.props.fields, {
        field: field.field
      });
      const baseFieldInForm = findOne(baseSchemaForm.props.fields, {
        field: field.field
      });
      if (!thisField || !baseFieldInForm) continue;
      if (
        (!isRequireDisabled(p.field, true, NodeType.Item) &&
          thisField.requiredAt !== baseFieldInForm.requiredAt) ||
        (!isShowDisabled(p.field) &&
          thisField.showAt !== baseFieldInForm.showAt)
      ) {
        manageDiff = true;
        break;
      }
    }

    return configureDiff || manageDiff;
  };

  const editableFields = ['name', 'description'];

  const isEdited =
    isConfigurePage &&
    (isSystemField || isInherited) &&
    isFieldSettingEdited(p.field, baseSchemaField, editableFields);

  const isDisabled = p.isDisabled || isManagePage;
  const {
    ref: dndRef,
    opacity,
    handlerId
  } = dndCardDetails(onMove, field, index);

  return (
    <>
      <div
        className={cn({
          'selected-field': isSelected,
          'disabled-field': isDisabled,
          'error-field': !!errMsg
        })}
        ref={ref}
        style={{ opacity }}
        data-handler-id={handlerId}
      >
        <div
          className="card"
          onClick={() => selectField(field)}
          ref={dndRef}
          style={{ opacity }}
          data-handler-id={handlerId}
        >
          <div className="field-col">
            <span>{name || '--'}</span>{' '}
            {isConfigurePage &&
              (!baseSchemaIsSystem ||
                description != inheritedDesc ||
                name != inheritedName) && (
                <span className="edited">{isEdited && '(edited)'}</span>
              )}
          </div>
          <div className="field-col">{fieldTypeName || '--'}</div>
          {isConfigurePage && (
            <div className="field-col">
              {showInValues?.map(v => itemTypes[v]).join(', ')}
            </div>
          )}
          {isManagePage && (
            <>
              <div className="field-col">{requiredValue?.label}</div>
              <div className="field-col">{showValue?.label}</div>
            </>
          )}
          {isSelected && (
            <div className="field-form ">
              <Group
                className="name"
                label={GroupLabel.name}
                componentRight={
                  isConfigurePage && (isSystemField || isInherited) ? (
                    <div className="icon-bar">
                      {!isSystemTitleEditable && (
                        <TooltipPopover
                          type={'info'}
                          content={'Override'}
                          packed={true}
                        >
                          <Icon
                            onClick={() => {
                              setIsSystemTitleEditable(true);
                            }}
                          >
                            <Pencil />
                          </Icon>
                        </TooltipPopover>
                      )}
                      {isSystemTitleEditable && <div></div>}

                      {name != inheritedName && (
                        <TooltipPopover
                          type={'info'}
                          content={`Revert to original name: ${inheritedName}`}
                          packed={true}
                        >
                          <Icon
                            onClick={() => {
                              updateField(field, { name: inheritedName });
                              setIsSystemTitleEditable(false);
                            }}
                          >
                            <Revert />
                          </Icon>
                        </TooltipPopover>
                      )}
                    </div>
                  ) : null
                }
              >
                <input
                  type="text"
                  className="form-control"
                  disabled={
                    isManagePage || (isDisabled && !isSystemTitleEditable)
                  }
                  value={name || ''}
                  onChange={e => updateField(field, { name: e.target.value })}
                />
              </Group>
              {isManagePage && (
                <ManageFields
                  schemaForm={schemaForm}
                  field={p.field}
                  itemRequiredOptions={itemRequiredOptions}
                />
              )}
              <Group label={GroupLabel.fieldType}>
                <Typeahead
                  value={fieldTypeName}
                  options={!!refId ? StringTypeOpions : FieldTypeOptions}
                  disabled={isDisabled || disableType}
                  showAllByDefault
                  includes={() => true}
                  onSelect={o => {
                    updateField(field, {
                      fieldType: o.fieldType,
                      dataType: o.dataType,
                      requiredAt: FieldRequiredValue.NotRequired,
                      multiValue: undefined
                    });
                  }}
                  renderOption={o => {
                    return (
                      <span className="field-type-option">
                        <o.Icon />
                        {o.name}
                      </span>
                    );
                  }}
                />
              </Group>
              {isConfigurePage && (
                <Group label={GroupLabel.showIn}>
                  <EnumInput
                    enumValues={itemTypes}
                    selected={showInValues}
                    onChange={vals =>
                      handleShowInOnChange(field, vals, p.treeFields)
                    }
                    disabled={disableShowIn}
                    multiple
                    disabledOptions={alwaysShowIn}
                  />
                </Group>
              )}
              {fieldType == FieldType.reference && (
                <>
                  <Group label={GroupLabel.relationType}>
                    <Selection
                      value={relationType}
                      disabled={isDisabled || disableType}
                      onChange={(e: any) => {
                        const v = e.target.value;
                        const multiValue =
                          v == 'CHILD_OF'
                            ? false
                            : v == 'PARENT_OF'
                              ? true
                              : undefined;
                        if (!v) {
                          updateField(field, {
                            relationType: null,
                            multiValue
                          });
                        } else {
                          updateField(field, { relationType: v, multiValue });
                        }
                      }}
                      options={getRelationTypes(NodeType.Item, relationType)}
                    />
                  </Group>
                  <Group label={GroupLabel.dataType}>
                    <Typeahead
                      value={selectedDataTypeName}
                      options={dataTypes.filter(
                        dt => !(dt.dataType == NodeType.Item)
                      )}
                      disabled={isDisabled || disableType}
                      showAllByDefault
                      onSelect={o =>
                        updateField(field, {
                          dataType: o.dataType
                        })
                      }
                      renderOption={o => o.name}
                    />
                  </Group>
                </>
              )}
              {isConfigurePage && !treeFields && (
                <PermissionField
                  schema={schema}
                  f={p.field}
                  onChange={editableCriteria =>
                    updateField(field, { editableCriteria })
                  }
                />
              )}
              {!treeFields && (
                <Group
                  label={GroupLabel.description}
                  className="schema-field-description"
                  componentRight={
                    isConfigurePage && (isSystemField || isInherited) ? (
                      <div className="icon-bar">
                        {!isSystemDescriptionEditable && (
                          <TooltipPopover
                            type={'info'}
                            content={'Override'}
                            packed={true}
                          >
                            <Icon
                              onClick={() => {
                                setIsSystemDescriptionEditable(true);
                              }}
                            >
                              <Pencil />
                            </Icon>
                          </TooltipPopover>
                        )}
                        {isSystemDescriptionEditable && <div></div>}

                        {(!!description || !!inheritedDesc) &&
                          description != inheritedDesc && (
                            <TooltipPopover
                              type={'info'}
                              content={
                                <WYSIWYGEditor
                                  content={`Revert to original description: ${
                                    inheritedDesc || '--'
                                  }`}
                                  readonly={true}
                                  disabled={true}
                                  onChange={null}
                                />
                              }
                              packed={true}
                            >
                              <Icon
                                onClick={() => {
                                  updateField(field, {
                                    description: inheritedDesc
                                  });
                                  setIsSystemDescriptionEditable(false);
                                }}
                              >
                                <Revert />
                              </Icon>
                            </TooltipPopover>
                          )}
                      </div>
                    ) : null
                  }
                >
                  <WYSIWYGEditor
                    content={
                      description
                        ? description
                        : isDisabled && !isSystemDescriptionEditable
                          ? '--'
                          : ''
                    }
                    disabled={
                      isManagePage ||
                      (isDisabled && !isSystemDescriptionEditable)
                    }
                    readonly={
                      isManagePage ||
                      (isDisabled && !isSystemDescriptionEditable)
                    }
                    onChange={e => updateField(field, { description: e })}
                  />
                </Group>
              )}
              <Group className="field-without-label">
                <div className="field-togglers">
                  {fieldType == FieldType.reference && (
                    <Toggler
                      label={GroupLabel.multiValue}
                      disabled={
                        isDisabled ||
                        relationType == 'PARENT_OF' ||
                        relationType == 'CHILD_OF' ||
                        relationType == 'EQUIVALENT'
                      }
                      checked={multiValue}
                      onChange={v => updateField(field, { multiValue: v })}
                    />
                  )}
                </div>
                {isConfigurePage &&
                  (!fieldType || fieldType == FieldType.static) && (
                    <Toggler
                      styles={{ marginTop: '10px' }}
                      label={GroupLabel.isOrderField}
                      checked={isOrderField}
                      onChange={v => updateField(field, { isOrderField: v })}
                    />
                  )}
              </Group>
            </div>
          )}
          <div className="field-card-dots">{!!onMove && <SixDots />}</div>
          {isConfigurePage && isEdited && isSelected && !baseSchemaIsSystem && (
            <div>
              <TooltipPopover
                type="info"
                content={getRevertTooltipContent(
                  p.field,
                  baseSchemaField,
                  editableFields,
                  schema,
                  showInValues,
                  inheritedShowInValues,
                  itemTypes,
                  schemaForms
                )}
                packed={true}
              >
                <span
                  className="icon-bar"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    updateField(field, {
                      name: inheritedName,
                      description: inheritedDesc
                    });
                    if (!disableShowIn) {
                      handleShowInOnChange(
                        field,
                        inheritedShowInValues,
                        p.treeFields
                      );
                    }
                    setIsSystemTitleEditable(false);
                    setIsSystemDescriptionEditable(false);
                    schemaForms?.forEach(schemaForm => {
                      const baseSchemaForm =
                        schema?.baseSchema?.schemaForms?.find(
                          f => f.props.itemType == schemaForm.props.itemType
                        );
                      if (!baseSchemaForm) return;
                      const thisField = findOne(schemaForm.props.fields, {
                        field: p.field.field
                      });
                      const baseField = findOne(baseSchemaForm.props.fields, {
                        field: p.field.field
                      });
                      if (!thisField || !baseField) return;
                      if (
                        !isRequireDisabled(p.field, true, NodeType.Item) &&
                        thisField.requiredAt !== baseField.requiredAt
                      ) {
                        updateField(
                          field,
                          { requiredAt: baseField.requiredAt },
                          schemaForm
                        );
                      }
                      if (
                        !isShowDisabled(p.field) &&
                        thisField.showAt !== baseField.showAt
                      ) {
                        updateField(
                          field,
                          { showAt: baseField.showAt },
                          schemaForm
                        );
                      }
                    });
                  }}
                >
                  <Revert />
                </span>
              </TooltipPopover>
            </div>
          )}
        </div>
        {isConfigurePage && (
          <RemoveField
            field={p.field}
            removeField={removeField}
            canRemoveField={!isDisabled && field != displayField}
          />
        )}
        {p.children}
      </div>
      {!!errMsg && <div className="field-error">{errMsg}</div>}
    </>
  );
};

const RemoveField = ({ field, removeField, canRemoveField }) => {
  const viewName = useSelector(s => s.schemaModal?.name);

  const { confirm, ConfirmModal: DeleteModal } = useConfirmModal({
    label: 'Delete'
  });

  if (!canRemoveField) return <div></div>;

  const handleSelect = () => {
    confirm(
      <>
        This will remove the field
        {viewName ? ` from the ${viewName} form` : ''}. Are you sure you want to
        delete the&nbsp;
        <b>{field.name}</b> field?
      </>,
      {
        header: 'Delete Field',
        onDelete: () => {
          removeField(field.field);
        }
      }
    );
  };

  return (
    <div>
      <div className="field-actions-menu-container">
        <div className="delete-modal-wrapper">
          <DeleteModal />
        </div>
        <div onClick={handleSelect} className={'three-dots'}>
          <TrashSmallGrey />
        </div>
      </div>
    </div>
  );
};

type ManageFieldsProps = {
  schemaForm: Schema;
  field: SchemaField;
  itemRequiredOptions: { value: string; label: string }[];
};

const ManageFields = (p: ManageFieldsProps) => {
  const { schemaForm, itemRequiredOptions } = p;

  const { fieldType, relationType, field, requiredAt, showAt } = p.field;

  const { updateField } = useActions();

  const showOptions = getItemManageShowOptions(requiredAt);

  React.useEffect(() => {
    if (requiredAt) {
      if (!findOne(showOptions, { value: showAt })) {
        updateField(field, { showAt: FieldShowValue.IdeaCreation }, schemaForm);
      }
    }
  }, [requiredAt]);

  const showRequiredAt =
    ([FieldType.reference, FieldType.static].includes(fieldType) &&
      !isAuditType(relationType)) ||
    DataType.dataSourceProjects == p?.field?.dataType;

  return (
    <>
      {showRequiredAt && (
        <Group label={GroupLabel.requiredAt}>
          <Selection
            value={requiredAt}
            disabled={isRequireDisabled(p.field, true, NodeType.Item)}
            onChange={(e: any) =>
              updateField(field, { requiredAt: e.target.value }, schemaForm)
            }
            options={itemRequiredOptions}
          />
        </Group>
      )}
      <Group label={GroupLabel.showAt}>
        <Selection
          value={showAt}
          disabled={isShowDisabled(p.field)}
          onChange={(e: any) =>
            updateField(field, { showAt: e.target.value }, schemaForm)
          }
          options={showOptions}
        />
      </Group>
    </>
  );
};

const ItemManageShowOptions = [
  { value: FieldShowValue.IdeaCreation, label: 'Idea creation' },
  { value: FieldShowValue.Idea, label: 'Idea' },
  { value: FieldShowValue.ConceptReview, label: 'Concept Review' },
  { value: FieldShowValue.Hide, label: 'Not Show' }
];

const getItemManageShowOptions = (requiredAt: FieldRequiredValue) => {
  switch (requiredAt) {
    case FieldRequiredValue.Idea:
      return ItemManageShowOptions.slice(0, 1);
    case FieldRequiredValue.ConceptReview:
      return ItemManageShowOptions.slice(0, 2);
    case FieldRequiredValue.ReviewDone:
      return ItemManageShowOptions.slice(0, 3);
  }
  return ItemManageShowOptions;
};

export const GroupLabel = {
  name: 'Field Name',
  fieldType: 'Field Type',
  showIn: 'Show In',
  relationType: 'Relation Type',
  dataType: 'Data Type',
  description: 'Description',
  multiValue: 'Allow Multiple Values',
  isOrderField: 'Display Order',
  requiredAt: 'Required to Enter',
  showAt: 'Show Starting At',
  treeGroupName: 'Grouped Field Name'
};

export const getRevertTooltipContent = (
  field: SchemaField,
  baseField: SchemaField,
  editableFields: string[],
  schema: Schema,
  showInValues?,
  inheritedShowInValues?,
  itemTypes?,
  schemaForms?,
  disableShowIn?
) => {
  const diffs = editableFields.filter(key => field[key] !== baseField[key]);
  const manageTabDiffs: Record<string, string[]> = {};
  if (schemaForms) {
    schemaForms.forEach(schemaForm => {
      const baseSchemaForm = schema?.baseSchema?.schemaForms?.find(
        f => f.props.itemType == schemaForm.props.itemType
      );
      if (!baseSchemaForm) return;
      const thisField = findOne(schemaForm.props.fields, {
        field: field.field
      });
      const baseField = findOne(baseSchemaForm.props.fields, {
        field: field.field
      });
      if (!thisField || !baseField) return;
      let diffKeys = [];
      if (
        !isRequireDisabled(field, true, NodeType.Item) &&
        thisField['requiredAt'] !== baseField.requiredAt
      ) {
        diffKeys.push('requiredAt');
      }
      if (!isShowDisabled(field) && thisField['showAt'] !== baseField.showAt) {
        diffKeys.push('showAt');
      }
      if (diffKeys.length) {
        manageTabDiffs[schemaForm.props.itemType] = diffKeys;
      }
    });
  }

  const showInDiffers =
    !disableShowIn &&
    JSON.stringify((showInValues || []).slice().sort()) !==
      JSON.stringify((inheritedShowInValues || []).slice().sort());

  return (
    <>
      {(diffs.length > 0 || showInDiffers) && (
        <>
          <b>General</b>
          <div>
            {diffs?.map(key => (
              <div key={key}>{GroupLabel[key] || key}</div>
            ))}
            {showInDiffers && <div>Show In</div>}
          </div>
        </>
      )}
      {Object.entries(manageTabDiffs)?.map(([itemType, keys]) => (
        <div key={itemType}>
          <b>{itemTypes[itemType]}</b>
          <div>
            {keys.map(key => (
              <div key={key}>{GroupLabel[key] || key}</div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};
